/**
 * ClipboardInspector.tsx - Clipboard inspector component
 * 
 * CHANGE HISTORY:
 * - 251102_1210 [pending] danielbeer: Created clipboard inspector for Clippr rebuild
 * 
 * React component for inspecting clipboard contents, history, and slots.
 */

import { useState } from 'react';
import { useClippr } from './useClippr';
import type { ClipboardItem } from '../types';

export interface ClipboardInspectorProps {
  className?: string;
  showHistory?: boolean;
  showSlots?: boolean;
  showStats?: boolean;
  showFileMaker?: boolean;
  maxHistoryItems?: number;
}

/**
 * Clipboard Inspector Component
 * Displays clipboard contents, history, slots, and statistics
 */
export function ClipboardInspector({
  className = '',
  showHistory = true,
  showSlots = true,
  showStats = true,
  showFileMaker = true,
  maxHistoryItems = 10
}: ClipboardInspectorProps) {
  const clippr = useClippr();
  const [selectedTab, setSelectedTab] = useState<'current' | 'history' | 'slots' | 'stats'>('current');
  
  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = Date.now();
    const diff = now - timestamp;
    
    // Less than 1 minute
    if (diff < 60000) {
      return 'just now';
    }
    // Less than 1 hour
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000);
      return `${mins}m ago`;
    }
    // Less than 1 day
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    }
    // Less than 1 week
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return `${days}d ago`;
    }
    
    return date.toLocaleDateString();
  };
  
  const formatDataPreview = (item: ClipboardItem) => {
    const data = item.data;
    
    if (typeof data === 'string') {
      return data.length > 100 ? data.substring(0, 100) + '...' : data;
    }
    
    if (typeof data === 'object') {
      return JSON.stringify(data, null, 2).substring(0, 100) + '...';
    }
    
    return String(data);
  };
  
  const renderCurrent = () => {
    if (!clippr.hasData) {
      return (
        <div className="clipboard-empty">
          <p>Clipboard is empty</p>
        </div>
      );
    }
    
    const item = clippr.clipboard;
    if (!item) return null;
    
    return (
      <div className="clipboard-current">
        <div className="clipboard-item">
          <div className="clipboard-item-header">
            <span className="format-badge">{item.format}</span>
            <span className="operation-badge">{item.operation}</span>
            <span className="timestamp">{formatTimestamp(item.timestamp)}</span>
          </div>
          
          <div className="clipboard-item-data">
            <pre>{formatDataPreview(item)}</pre>
          </div>
          
          {item.metadata.slot && (
            <div className="clipboard-item-meta">
              <strong>Slot:</strong> {item.metadata.slot}
            </div>
          )}
          
          {showFileMaker && clippr.hasFileMakerXML() && (
            <div className="clipboard-filemaker">
              <strong>FileMaker Object Detected</strong>
              {(() => {
                const fmData = clippr.getFileMakerMetadata();
                if (fmData) {
                  return (
                    <div className="filemaker-meta">
                      <div><strong>Type:</strong> {fmData.type}</div>
                      {fmData.name && <div><strong>Name:</strong> {fmData.name}</div>}
                      {fmData.id && <div><strong>ID:</strong> {fmData.id}</div>}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  const renderHistory = () => {
    const items = clippr.history.slice(0, maxHistoryItems);
    
    if (items.length === 0) {
      return (
        <div className="clipboard-empty">
          <p>No history</p>
        </div>
      );
    }
    
    return (
      <div className="clipboard-history">
        {items.map((item, index) => (
          <div key={item.id} className="clipboard-history-item">
            <div className="clipboard-item-header">
              <span className="index">#{index + 1}</span>
              <span className="format-badge">{item.format}</span>
              <span className="timestamp">{formatTimestamp(item.timestamp)}</span>
            </div>
            
            <div className="clipboard-item-data">
              <pre>{formatDataPreview(item)}</pre>
            </div>
            
            <button
              onClick={() => clippr.pasteFromHistory(index)}
              className="paste-button"
            >
              Paste
            </button>
          </div>
        ))}
      </div>
    );
  };
  
  const renderSlots = () => {
    const slotNames = clippr.slots;
    
    if (slotNames.length === 0) {
      return (
        <div className="clipboard-empty">
          <p>No slots</p>
        </div>
      );
    }
    
    return (
      <div className="clipboard-slots">
        {slotNames.map((slotName) => {
          const hasData = clippr.peek(slotName) !== null;
          
          return (
            <div key={slotName} className={`clipboard-slot ${hasData ? 'has-data' : 'empty'}`}>
              <div className="slot-header">
                <strong>{slotName}</strong>
                {hasData && <span className="status-badge">Has Data</span>}
              </div>
              
              {hasData && (
                <div className="slot-actions">
                  <button onClick={() => clippr.paste({ slot: slotName })}>
                    Paste
                  </button>
                  <button onClick={() => clippr.clear(slotName)}>
                    Clear
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };
  
  const renderStats = () => {
    const { stats } = clippr;
    
    return (
      <div className="clipboard-stats">
        <div className="stat-group">
          <h3>Operations</h3>
          <div className="stat-item">
            <span className="stat-label">Copies:</span>
            <span className="stat-value">{stats.totalCopies}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Cuts:</span>
            <span className="stat-value">{stats.totalCuts}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Pastes:</span>
            <span className="stat-value">{stats.totalPastes}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Clears:</span>
            <span className="stat-value">{stats.totalClears}</span>
          </div>
        </div>
        
        <div className="stat-group">
          <h3>Storage</h3>
          <div className="stat-item">
            <span className="stat-label">History Size:</span>
            <span className="stat-value">{stats.historySize}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Active Slots:</span>
            <span className="stat-value">{stats.activeSlots}</span>
          </div>
        </div>
        
        <div className="stat-group">
          <h3>Format Distribution</h3>
          {Object.entries(stats.formatDistribution).map(([format, count]) => (
            <div key={format} className="stat-item">
              <span className="stat-label">{format}:</span>
              <span className="stat-value">{count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  return (
    <div className={`clipboard-inspector ${className}`}>
      <div className="clipboard-inspector-header">
        <h2>Clipboard Inspector</h2>
        <button onClick={clippr.refresh} className="refresh-button">
          Refresh
        </button>
      </div>
      
      <div className="clipboard-inspector-tabs">
        <button
          className={selectedTab === 'current' ? 'active' : ''}
          onClick={() => setSelectedTab('current')}
        >
          Current
        </button>
        
        {showHistory && (
          <button
            className={selectedTab === 'history' ? 'active' : ''}
            onClick={() => setSelectedTab('history')}
          >
            History ({clippr.history.length})
          </button>
        )}
        
        {showSlots && (
          <button
            className={selectedTab === 'slots' ? 'active' : ''}
            onClick={() => setSelectedTab('slots')}
          >
            Slots ({clippr.slots.length})
          </button>
        )}
        
        {showStats && (
          <button
            className={selectedTab === 'stats' ? 'active' : ''}
            onClick={() => setSelectedTab('stats')}
          >
            Stats
          </button>
        )}
      </div>
      
      <div className="clipboard-inspector-content">
        {selectedTab === 'current' && renderCurrent()}
        {selectedTab === 'history' && renderHistory()}
        {selectedTab === 'slots' && renderSlots()}
        {selectedTab === 'stats' && renderStats()}
      </div>
    </div>
  );
}
